import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, onValue } from 'firebase/database';
import { useNames } from '../userNames';
import { displayPlace, departure } from '../places';
import { REPORT_REASONS } from '../safety';
import { isAdmin, removePost, removePhoto, setSuspended, resolveReport, saveSponsor, uploadSponsorImage, findDriver, postSponsoredRide } from '../admin';
import './Admin.css';

const REMOVE_REASONS = ['Spam', 'Unsafe', 'Violates the Terms of Use'];

const reasonLabel = value => REPORT_REASONS.find(r => r.value === value)?.label || value;
const when = ms => (ms ? new Date(ms).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');

/** The list at a database path as [{ id, ...value }]. */
function useList(path) {
  const [items, setItems] = useState(null);
  useEffect(() => onValue(ref(getDatabase(), path),
    s => setItems(Object.entries(s.val() || {}).map(([id, v]) => ({ ...v, id }))),
    () => setItems([])), [path]);
  return items;
}

/** The node at a path as an object. */
function useNode(path) {
  const [value, setValue] = useState({});
  useEffect(() => onValue(ref(getDatabase(), path), s => setValue(s.val() || {}), () => setValue({})), [path]);
  return value;
}

/** Asks for a reason, then runs `action(reason)`. */
function ReasonDialog({ title, confirm, onClose, action }) {
  const [choice, setChoice] = useState(REMOVE_REASONS[0]);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const reason = choice === 'other' ? custom.trim() : choice;

  const go = async () => {
    setBusy(true); setError('');
    try {
      await action(reason);
      onClose();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="admin-dialog-back" role="dialog" aria-modal="true" aria-label={title}>
      <div className="admin-dialog">
        <h2>{title}</h2>
        <p className="admin-muted">People affected are told this reason.</p>
        {[...REMOVE_REASONS, 'other'].map(r => (
          <label key={r} className="admin-radio">
            <input type="radio" name="reason" checked={choice === r} onChange={() => setChoice(r)} />
            {r === 'other' ? 'Something else' : r}
          </label>
        ))}
        {choice === 'other' && (
          <input className="admin-input" value={custom} maxLength={200} autoFocus
                 placeholder="Reason" onChange={e => setCustom(e.target.value)} />
        )}
        {error && <p className="admin-error">{error}</p>}
        <div className="admin-actions">
          <button className="admin-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="admin-btn danger" onClick={go} disabled={busy || !reason}>{busy ? 'Working' : confirm}</button>
        </div>
      </div>
    </div>
  );
}

function ReportsTab({ names, suspensions, ask }) {
  const reports = useList('reports');
  const [showResolved, setShowResolved] = useState(false);
  const [error, setError] = useState('');
  const shown = (reports || [])
    .filter(r => showResolved || !r.resolvedAt)
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const run = fn => async () => {
    setError('');
    try { await fn(); } catch (e) { setError(e.message); }
  };

  if (!reports) return <p className="admin-muted">Loading reports</p>;
  return (
    <>
      <label className="admin-radio">
        <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} />
        Show resolved
      </label>
      {error && <p className="admin-error">{error}</p>}
      {shown.length === 0 && <p className="admin-muted">No open reports.</p>}
      {shown.map(r => {
        const node = r.contextType === 'request' ? 'rideRequest' : 'rideReserve';
        const canRemove = r.contextId && (r.contextType === 'ride' || r.contextType === 'request');
        const isSuspended = !!suspensions[r.reported];
        return (
          <article key={r.id} className="admin-card">
            <div className="admin-card-top">
              <strong>{reasonLabel(r.reason)}</strong>
              <span className="admin-muted">{when(r.createdAt)}</span>
            </div>
            <p className="admin-line">
              {names[r.reporter] || r.reporter} reported <strong>{names[r.reported] || r.reported}</strong>
              {r.contextType && ` · from a ${r.contextType}`}
            </p>
            {r.note && <p className="admin-note">{r.note}</p>}
            {r.resolvedAt ? <p className="admin-muted">Resolved {when(r.resolvedAt)}</p> : (
              <div className="admin-actions start">
                {canRemove && <button className="admin-btn danger" onClick={() => ask({
                  title: 'Remove this post', confirm: 'Remove',
                  action: reason => removePost(node, r.contextId, reason),
                })}>Remove post</button>}
                {r.reason === 'profile_photo' && (
                  <button className="admin-btn danger" onClick={run(() => removePhoto(r.reported))}>Remove photo</button>
                )}
                {isSuspended
                  ? <button className="admin-btn" onClick={run(() => setSuspended(r.reported, false))}>Lift suspension</button>
                  : <button className="admin-btn danger" onClick={() => ask({
                      title: `Suspend ${names[r.reported] || 'this account'}`, confirm: 'Suspend',
                      action: reason => setSuspended(r.reported, true, reason),
                    })}>Suspend</button>}
                <button className="admin-btn" onClick={run(() => resolveReport(r.id))}>Mark resolved</button>
              </div>
            )}
          </article>
        );
      })}
    </>
  );
}

function PostsTab({ names, ask }) {
  const rides = useList('rideReserve');
  const requests = useList('rideRequest');
  const [search, setSearch] = useState('');

  const posts = useMemo(() => {
    const now = Date.now();
    const q = search.trim().toLowerCase();
    return [
      ...(rides || []).map(p => ({ ...p, node: 'rideReserve' })),
      ...(requests || []).map(p => ({ ...p, node: 'rideRequest' })),
    ]
      .map(p => ({ ...p, leaves: departure(p)?.getTime() ?? 0 }))
      .filter(p => p.leaves > now)
      .filter(p => !q || [names[p.userID], p.fromLocation, p.toLocation].some(v => (v || '').toLowerCase().includes(q)))
      .sort((a, b) => a.leaves - b.leaves);
  }, [rides, requests, search, names]);

  if (!rides || !requests) return <p className="admin-muted">Loading posts</p>;
  return (
    <>
      <input className="admin-input" type="search" placeholder="Search by name or place" aria-label="Search posts"
             value={search} onChange={e => setSearch(e.target.value)} />
      {posts.length === 0 && <p className="admin-muted">No upcoming posts match.</p>}
      {posts.map(p => (
        <article key={`${p.node}-${p.id}`} className="admin-card admin-post">
          <div>
            <strong>{displayPlace(p.fromLocation)} → {displayPlace(p.toLocation)}</strong>
            <p className="admin-line">
              {p.node === 'rideRequest' ? 'Request' : 'Ride'} · {names[p.userID] || 'Loading'} · {when(p.leaves)}
            </p>
          </div>
          <button className="admin-btn danger" onClick={() => ask({
            title: 'Remove this post', confirm: 'Remove',
            action: reason => removePost(p.node, p.id, reason),
          })}>Remove</button>
        </article>
      ))}
    </>
  );
}

const SPONSOR_TEXT = [
  ['name', 'Sponsor name', 'Wild Blue Merch', 60],
  ['headline', 'Banner caption', '10% off USAFA merch', 80],
  ['code', 'Discount code', 'FALCONRIDE', 30],
  ['url', 'Link', 'https://example.com', 500],
  ['rideLabel', 'Ride card banner text', 'Sponsored Ride', 40],
];
const SPONSOR_IMAGES = [
  ['imageURL', 'Banner photo (square)', 'Home ad on the app and phones'],
  ['wideImageURL', 'Wide banner photo', 'Desktop website Home'],
  ['logoURL', 'Ride card logo', 'Wide, transparent PNG. Blank uses the built-in Wild Blue logo'],
  ['markURL', 'Small logo', 'Square, transparent PNG. Blank uses the built-in Wild Blue mark'],
];

/** One image setting: preview, upload, or clear. */
function SponsorImage({ field, label, hint, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setError('');
    try { onChange(field, await uploadSponsorImage(file)); } catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <div className="admin-image">
      <div className="admin-thumb">{value ? <img src={value} alt="" /> : <span>None</span>}</div>
      <div className="admin-image-main">
        <strong>{label}</strong>
        <p className="admin-muted">{hint}</p>
        <div className="admin-actions start">
          <label className="admin-btn">
            {busy ? 'Uploading' : 'Upload'}
            <input type="file" accept="image/*" hidden disabled={busy} onChange={pick} />
          </label>
          {value && <button type="button" className="admin-btn" onClick={() => onChange(field, '')}>Remove</button>}
        </div>
        {error && <p className="admin-error">{error}</p>}
      </div>
    </div>
  );
}

const clockText = (value) => {
  const [h, m] = value.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

/** Post a free ride under a driver's account, and see or remove current ones. */
function SponsoredRides({ ask }) {
  const rides = useList('rideReserve');
  const [username, setUsername] = useState('');
  const [driver, setDriver] = useState(null);
  const [form, setForm] = useState({ from: '', to: '', day: '', time: '', seats: 1 });
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (field, value) => { setStatus(''); setForm(f => ({ ...f, [field]: value })); };

  const lookUp = async () => {
    setBusy(true); setStatus('');
    try {
      const found = await findDriver(username);
      setDriver(found);
      if (!found) setStatus('No one has that username. It must match exactly.');
    } catch (e) { setStatus(e.message); }
    setBusy(false);
  };

  const post = async () => {
    setBusy(true); setStatus('');
    try {
      await postSponsoredRide({ ...form, seats: Number(form.seats), time: clockText(form.time), driverUid: driver.uid });
      setForm({ from: '', to: '', day: '', time: '', seats: 1 });
      setStatus('Posted. It is live now.');
    } catch (e) { setStatus(e.message); }
    setBusy(false);
  };

  const carText = driver?.car && [driver.car.color, driver.car.make, driver.car.model].filter(Boolean).join(' ');
  const current = (rides || [])
    .filter(r => r.sponsor)
    .map(r => ({ ...r, leaves: departure(r)?.getTime() ?? 0 }))
    .filter(r => r.leaves > Date.now())
    .sort((a, b) => a.leaves - b.leaves);
  const ready = driver && form.from.trim() && form.to.trim() && form.day && form.time;

  return (
    <article className="admin-card">
      <strong>Post a sponsored ride</strong>
      <p className="admin-muted">Posts under the driver, so bookings and texts go to them. Agree it with them first. Times are Mountain.</p>
      <label className="admin-field">
        Driver's username
        <span className="admin-row">
          <input className="admin-input" value={username} placeholder="Fifty150"
                 onChange={e => { setUsername(e.target.value); setDriver(null); setStatus(''); }} />
          <button type="button" className="admin-btn" onClick={lookUp} disabled={busy || !username.trim()}>Find</button>
        </span>
      </label>
      {driver && <p className="admin-line">Driver: <strong>{driver.name}</strong>{carText ? ` · ${carText}` : ''}</p>}
      <div className="admin-row">
        <label className="admin-field grow">From<input className="admin-input" value={form.from} maxLength={100} placeholder="USAFA" onChange={e => set('from', e.target.value)} /></label>
        <label className="admin-field grow">To<input className="admin-input" value={form.to} maxLength={100} placeholder="Denver Airport (DEN)" onChange={e => set('to', e.target.value)} /></label>
      </div>
      <div className="admin-row">
        <label className="admin-field grow">Date<input className="admin-input" type="date" value={form.day} onChange={e => set('day', e.target.value)} /></label>
        <label className="admin-field grow">Time<input className="admin-input" type="time" value={form.time} onChange={e => set('time', e.target.value)} /></label>
        <label className="admin-field">Free seats<input className="admin-input" type="number" min="1" max="9" value={form.seats} onChange={e => set('seats', e.target.value)} /></label>
      </div>
      {status && <p className="admin-muted" role="status">{status}</p>}
      <div className="admin-actions">
        <button className="admin-btn primary" onClick={post} disabled={busy || !ready}>{busy ? 'Posting' : 'Post ride'}</button>
      </div>

      {current.length > 0 && <strong>Upcoming sponsored rides</strong>}
      {current.map(r => (
        <div key={r.id} className="admin-post">
          <p className="admin-line">
            {displayPlace(r.fromLocation)} → {displayPlace(r.toLocation)} · {r.userName} · {when(r.leaves)} · {r.seats} left
          </p>
          <button className="admin-btn danger" onClick={() => ask({
            title: 'Remove this ride', confirm: 'Remove',
            action: reason => removePost('rideReserve', r.id, reason),
          })}>Remove</button>
        </div>
      ))}
    </article>
  );
}

function SponsorTab({ ask }) {
  const saved = useNode('config/sponsor');
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const form = draft || saved;
  const set = (field, value) => { setStatus(''); setDraft({ ...form, [field]: value }); };

  const save = async () => {
    setBusy(true); setStatus('');
    const { active, ...settings } = form;
    if (!settings.name?.trim()) { setStatus('Add a sponsor name.'); setBusy(false); return; }
    if (settings.url && !settings.url.startsWith('https://')) { setStatus('The link must start with https://'); setBusy(false); return; }
    try {
      await saveSponsor({ ...settings, active: active === true });
      setDraft(null);
      setStatus('Saved. It is live now.');
    } catch (e) { setStatus(e.message); }
    setBusy(false);
  };

  const toggle = async () => {
    setBusy(true); setStatus('');
    try {
      await saveSponsor({ active: saved.active !== true });
    } catch (e) { setStatus(e.message); }
    setBusy(false);
  };

  return (
    <>
      <article className="admin-card admin-post">
        <div>
          <strong>Home ad is {saved.active ? 'on' : 'off'}</strong>
          <p className="admin-line">Sponsored rides keep their look either way.</p>
        </div>
        <button className={`admin-btn${saved.active ? ' danger' : ''}`} onClick={toggle} disabled={busy || !saved.name}>
          {saved.active ? 'Turn off' : 'Turn on'}
        </button>
      </article>

      <article className="admin-card">
        {SPONSOR_TEXT.map(([field, label, placeholder, max]) => (
          <label key={field} className="admin-field">
            {label}
            <input className="admin-input" value={form[field] || ''} placeholder={placeholder} maxLength={max}
                   onChange={e => set(field, e.target.value)} />
          </label>
        ))}
        <label className="admin-field">
          Ride card color
          <span className="admin-color">
            <input type="color" value={form.accentColor || '#185FA5'} aria-label="Ride card color"
                   onChange={e => set('accentColor', e.target.value)} />
            <span className="admin-muted">{form.accentColor || 'Built-in Wild Blue color'}</span>
            {form.accentColor && <button type="button" className="admin-btn" onClick={() => set('accentColor', '')}>Reset</button>}
          </span>
        </label>
      </article>

      <article className="admin-card">
        {SPONSOR_IMAGES.map(([field, label, hint]) => (
          <SponsorImage key={field} field={field} label={label} hint={hint} value={form[field]} onChange={set} />
        ))}
      </article>

      <SponsoredRides ask={ask} />

      {status && <p className="admin-muted" role="status">{status}</p>}
      <div className="admin-actions">
        {draft && <button className="admin-btn" onClick={() => { setDraft(null); setStatus(''); }} disabled={busy}>Discard</button>}
        <button className="admin-btn primary" onClick={save} disabled={busy || !draft}>{busy ? 'Saving' : 'Save changes'}</button>
      </div>
    </>
  );
}

export default function Admin() {
  const me = getAuth().currentUser?.uid;
  const [tab, setTab] = useState('reports');
  const [dialog, setDialog] = useState(null);
  const allowed = isAdmin(me);
  const reports = useNode(allowed ? 'reports' : 'config/none');
  const rides = useNode(allowed ? 'rideReserve' : 'config/none');
  const requests = useNode(allowed ? 'rideRequest' : 'config/none');
  const suspensions = useNode(allowed ? 'suspensions' : 'config/none');
  const uids = useMemo(() => [
    ...Object.values(reports).flatMap(r => [r.reporter, r.reported]),
    ...Object.values(rides).map(r => r.userID),
    ...Object.values(requests).map(r => r.userID),
  ], [reports, rides, requests]);
  const names = useNames(uids);

  if (!allowed) return <Navigate replace to="/home" />;
  return (
    <div className="admin-page">
      <h1>Admin</h1>
      <div className="admin-tabs" role="tablist">
        {[['reports', 'Reports'], ['posts', 'Posts'], ['sponsor', 'Sponsor']].map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key}
                  className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>
      {tab === 'reports' && <ReportsTab names={names} suspensions={suspensions} ask={setDialog} />}
      {tab === 'posts' && <PostsTab names={names} ask={setDialog} />}
      {tab === 'sponsor' && <SponsorTab ask={setDialog} />}
      {dialog && <ReasonDialog {...dialog} onClose={() => setDialog(null)} />}
    </div>
  );
}
